import AntForm from "antd/es/form";
import { useUser } from "@shared/utils/hooks/misc.ts";
import useQuery from "utils/hooks/useQuery.ts";
import { EMAIL_QUERY_PARAM } from "features/settings/common/constants.ts";
import { useContext, useMemo, useState } from "react";
import { sharedLib } from "@shared/services/shared.service.ts";
import { SubscriptionPaymentMethod } from "@shared/features/settings/subscription/types.ts";
import { LOCAL_STORAGE_FAILED_PAYMENT_KEY } from "common/constants.ts";
import {
  GetPaymentLinkRequest,
  PaymentType,
  SubscriptionPaymentMethodType,
} from "@shared/types";
import { PAYMENT_OPTION_TO_DISPLAY_NAME } from "@shared/features/settings/subscription/constants.ts";
import { displayResultMessage } from "utils/misc.tsx";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import { telegramOpenLink } from "features/telegram/utils.ts";
import { getApiSlotsValidation } from "features/settings/common/apiSlotsValidation.ts";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";

const useSummary = () => {
  const { subscriptionPaymentMethod, paymentMethod, numApiSlots } = useContext(
    BuySubscriptionContext
  )!;
  const [form] = AntForm.useForm();
  const { user } = useUser();
  const query = useQuery();
  const queryEmail = query.get(EMAIL_QUERY_PARAM) || undefined;

  const [email, setEmail] = useState<string | undefined>(queryEmail);
  const userEmail = user?.email || email;

  const [getPaymentLink, getPaymentLinkStatus] =
    sharedLib.sharedApi.settings.useGetPaymentLinkMutation();

  const isCrypto =
    subscriptionPaymentMethod === SubscriptionPaymentMethod.CRYPTO;
  const isCreditCardPayment =
    subscriptionPaymentMethod === SubscriptionPaymentMethod.CREDIT_CARD;
  const isPaypal =
    subscriptionPaymentMethod === SubscriptionPaymentMethod.PAYPAL ||
    isCreditCardPayment;

  const openPaymentLink = (link: string) => {
    if (IS_TELEGRAM_WEB_APP) {
      try {
        telegramOpenLink(link);
      } catch {}
    } else {
      window.location.href = link;
    }
  };

  const { hasMinSlotsError, isInvalidStep } = getApiSlotsValidation(
    numApiSlots ?? 0
  );

  const baseRequest: GetPaymentLinkRequest | undefined = useMemo(() => {
    const paymentOption = isCrypto
      ? paymentMethod?.payment_option
      : PAYMENT_OPTION_TO_DISPLAY_NAME[SubscriptionPaymentMethodType.PAYPAL];
    if (!userEmail || !paymentOption || hasMinSlotsError || isInvalidStep)
      return;

    return {
      number_of_clients: numApiSlots || undefined,
      email: userEmail,
      payment_type: PaymentType.PRORATED_SLOTS,
      payment_option: paymentOption,
    };
  }, [userEmail, isCrypto, paymentMethod, numApiSlots]);

  const onGetPaymentLink = () => {
    if (!baseRequest) return;
    displayResultMessage(
      getPaymentLink({
        ...baseRequest,
        force_invoice: !!localStorage?.getItem(
          LOCAL_STORAGE_FAILED_PAYMENT_KEY
        ),
      }).unwrap()
    )
      .then((response) => {
        if (!isCrypto) {
          openPaymentLink(
            isCreditCardPayment
              ? response?.guest_payment_url
              : response?.payment_url
          );
        }
      })
      .catch(() => {});
  };

  return {
    onGetPaymentLink,
    form,
    history,
    isCrypto,
    isPaypal,
    email,
    setEmail,
    getPaymentLinkStatus,
    baseRequest,
    hasMinSlotsError,
    isInvalidStep,
  };
};

export default useSummary;
